import React, { useCallback } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import ListItemRadioButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemRadioButton';
import License from '@wix/da-shared-react/pkg/License';
import { fieldToLicense } from '../../fields/LicenseField/adapters';
import { SubmitFormValues } from '../../redux/types';
import type { FieldFormPopupState } from '../../modals/FieldFormPopup/types';

import s from './LicenseForm.scss';

export interface Props extends FieldFormPopupState<
  SubmitFormValues['license']
> {
  subheading?: string;
}

const LicenseForm: React.FC<Props> = ({
  currentValue: license,
  setValue: setLicense,
  subheading,
}) => {
  const { t } = useTranslation();

  const onChange = useCallback(
    (key: 'cc' | 'attr' | 'deriv', val: 'yes' | 'no' | 'share') => {
      // Ignore invalid changes
      if (key !== 'deriv' && val === 'share') {
        return false;
      }

      // Disallow changes of attr and deriv if CC is off
      if ((!license?.cc && key !== 'cc') || (key === 'cc' && val === 'no')) {
        setLicense({ cc: false, attr: false, deriv: 'no' });
        return false;
      }

      const nextLicense = {
        ...license,
        [key]: key === 'deriv' ? val : val === 'yes',
      };
      setLicense(nextLicense);
      return true;
    },
    [license, setLicense]
  );

  const isCc = license?.cc;

  return (
    <>
      {subheading && <p className={s['subheading']}>{subheading}</p>}
      <dl className={s['fields']}>
        <dt>{t('deviationSubmit.form.license.modal.question.cc')}</dt>
        <dd>
          <ListItemRadioButton
            className={s['radio']}
            label={t('deviationSubmit.form.license.modal.question.cc.yes')}
            name="license_cc"
            value="cc_yes"
            checked={isCc}
            onClick={() => onChange('cc', 'yes')}
          />
          <ListItemRadioButton
            className={s['radio']}
            label={t('deviationSubmit.form.license.modal.question.cc.no')}
            name="license_cc"
            value="cc_no"
            checked={!isCc}
            onClick={() => onChange('cc', 'no')}
          />
        </dd>
        <dt>{t('deviationSubmit.form.license.modal.question.attr')}</dt>
        <dd>
          <ListItemRadioButton
            className={s['radio']}
            label={t('deviationSubmit.form.license.modal.question.attr.yes')}
            name="license_attr"
            value="attr_yes"
            checked={isCc && license?.attr}
            readOnly={!isCc}
            disabled={!isCc}
            onClick={() => onChange('attr', 'yes')}
          />
          <ListItemRadioButton
            className={c(s['radio'], !isCc && s['radio-disabled'])}
            inputClassName={c(!isCc && s['radio-disabled'])}
            label={t('deviationSubmit.form.license.modal.question.attr.no')}
            name="license_attr"
            value="attr_no"
            checked={!(isCc && license?.attr)}
            readOnly={!isCc}
            disabled={!isCc}
            onClick={() => onChange('attr', 'no')}
          />
        </dd>
        <dt>{t('deviationSubmit.form.license.modal.question.deriv')}</dt>
        <dd>
          <ListItemRadioButton
            className={s['radio']}
            label={t('deviationSubmit.form.license.modal.question.deriv.yes')}
            name="license_deriv"
            value="deriv_yes"
            checked={isCc && license?.deriv === 'yes'}
            readOnly={!isCc}
            disabled={!isCc}
            onClick={() => onChange('deriv', 'yes')}
          />
          <ListItemRadioButton
            className={s['radio']}
            label={t('deviationSubmit.form.license.modal.question.deriv.share')}
            name="license_deriv"
            value="deriv_share"
            checked={isCc && license?.deriv === 'share'}
            readOnly={!isCc}
            disabled={!isCc}
            onClick={() => onChange('deriv', 'share')}
          />
          <ListItemRadioButton
            className={c(s['radio'], !isCc && s['radio-disabled'])}
            inputClassName={c(!isCc && s['radio-disabled'])}
            label={t('deviationSubmit.form.license.modal.question.deriv.no')}
            name="license_deriv"
            value="deriv_no"
            checked={!isCc || license?.deriv === 'no'}
            readOnly={!isCc}
            disabled={!isCc}
            onClick={() => onChange('deriv', 'no')}
          />
        </dd>
      </dl>
      {license && (
        <License
          className={s['sample']}
          showIcons
          license={fieldToLicense(license)}
        />
      )}
    </>
  );
};
LicenseForm.displayName = 'LicenseForm';

export default LicenseForm;
